// ============================================================
// SeletorLocal.js — escolha do local de treino no mapa
//
// Substitui o "cole as coordenadas do Google Maps", que é o tipo de passo
// em que alguém desiste ou cola errado. Aqui dá para buscar pelo endereço,
// arrastar o alfinete até o ponto certo, ou usar a localização do próprio
// aparelho quando se está no local.
//
// A busca acerta endereço com número e cidade; nome de pista ou ginásio
// costuma não existir na base do OpenStreetMap. Por isso o clique no mapa
// é o caminho garantido, e a busca é atalho.
//
// Por que OpenStreetMap e não Google Maps: o mapa do Google exige chave de
// API e conta de faturamento ativa. Para marcar meia dúzia de pistas, uma
// vez, isso é burocracia e custo sem retorno. O Leaflet com OSM não pede
// chave nenhuma, e a precisão de rua é a mesma para o que importa aqui:
// acertar um ponto dentro do raio configurado.
//
// O círculo do raio é desenhado junto de propósito: é ele que responde a
// pergunta que o coordenador realmente tem, que é se aquele ponto cobre a
// instalação inteira ou se precisa cadastrar um segundo.
// ============================================================

import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AZUL, TEXTO, CINZA_ESC, PERIGO } from "../tema";
import { lerCoordenadas } from "../coordenadas";

const RAIO_FIXO = 300;

// Fortaleza, quando ainda não há nada escolhido — o programa é do Ceará.
const CENTRO_PADRAO = { lat: -3.7319, lng: -38.5267 };

// Alfinete desenhado em CSS. O ícone padrão do Leaflet aponta para imagens
// que o empacotador do create-react-app não resolve, e o resultado é um
// marcador invisível — problema clássico e silencioso.
const ALFINETE = L.divIcon({
  className: "",
  html: `<div style="
    width:22px;height:22px;border-radius:50% 50% 50% 0;
    background:${AZUL};border:2.5px solid #fff;
    transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,.4);
  "></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

export default function SeletorLocal({ valor, onChange, alturaMapa = 260 }) {
  const divMapa = useRef(null);
  const mapa = useRef(null);
  const marcador = useRef(null);
  const circulo = useRef(null);

  const [busca, setBusca] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [colado, setColado] = useState("");

  const posicao = valor && Number.isFinite(valor.lat) ? valor : null;

  // Monta o mapa uma única vez.
  useEffect(() => {
    if (mapa.current || !divMapa.current) return;

    const inicial = posicao || CENTRO_PADRAO;
    // renderer explícito: o Leaflet cria o renderizador vetorial sob
    // demanda ao adicionar o círculo, e essa criação preguiçosa falha
    // quando o container ainda não tem tamanho — o que acontece enquanto
    // o cartão de edição está abrindo.
    const m = L.map(divMapa.current, { attributionControl: true, renderer: L.svg() })
      .setView([inicial.lat, inicial.lng], posicao ? 17 : 12);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap",
    }).addTo(m);

    // Clicar no mapa move o alfinete: é a forma mais direta de ajustar.
    m.on("click", (e) => onChange({ lat: e.latlng.lat, lng: e.latlng.lng }));

    mapa.current = m;

    // O container começa com tamanho zero enquanto o cartão abre; sem este
    // reajuste o mapa renderiza cinza pela metade.
    setTimeout(() => m.invalidateSize(), 60);

    return () => { m.remove(); mapa.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflete a posição escolhida no alfinete e no círculo de alcance.
  useEffect(() => {
    const m = mapa.current;
    if (!m) return;

    if (!posicao) {
      if (marcador.current) { marcador.current.remove(); marcador.current = null; }
      if (circulo.current) { circulo.current.remove(); circulo.current = null; }
      return;
    }

    const ll = [posicao.lat, posicao.lng];

    if (!marcador.current) {
      marcador.current = L.marker(ll, { icon: ALFINETE, draggable: true }).addTo(m);
      marcador.current.on("dragend", () => {
        const p = marcador.current.getLatLng();
        onChange({ lat: p.lat, lng: p.lng });
      });
    } else {
      marcador.current.setLatLng(ll);
    }

    if (!circulo.current) {
      circulo.current = L.circle(ll, {
        radius: RAIO_FIXO,
        color: AZUL, weight: 1.5, fillColor: AZUL, fillOpacity: 0.12,
      }).addTo(m);
    } else {
      circulo.current.setLatLng(ll);
    }
  }, [posicao, onChange]);

  async function buscarEndereco() {
    const termo = busca.trim();
    if (!termo) return;
    setBuscando(true);
    setAviso(null);
    try {
      // Nominatim é o serviço de busca do próprio OpenStreetMap. Gratuito e
      // sem chave; o "countrycodes=br" evita cair num homônimo em outro país.
      const url =
        "https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=br&q=" +
        encodeURIComponent(termo);
      const resp = await fetch(url, { headers: { Accept: "application/json" } });
      if (!resp.ok) throw new Error("busca indisponível");
      const achados = await resp.json();

      if (!achados.length) {
        setAviso({
          tipo: "erro",
          texto: "Não encontrei. A base de mapas costuma não ter o nome de pistas e ginásios, então busque pelo endereço com número e cidade, ou clique direto no mapa.",
        });
        return;
      }
      const { lat, lon, display_name: nome } = achados[0];
      const p = { lat: Number(lat), lng: Number(lon) };
      onChange(p);
      mapa.current?.setView([p.lat, p.lng], 17);
      setAviso({ tipo: "ok", texto: `Encontrado: ${nome}. Arraste o alfinete se precisar ajustar.` });
    } catch (e) {
      setAviso({ tipo: "erro", texto: "A busca de endereços não respondeu. Marque o ponto direto no mapa." });
    } finally {
      setBuscando(false);
    }
  }

  function usarMinhaLocalizacao() {
    if (!navigator.geolocation) {
      setAviso({ tipo: "erro", texto: "Este navegador não informa a localização." });
      return;
    }
    setAviso({ tipo: "info", texto: "Obtendo sua localização…" });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        onChange(p);
        mapa.current?.setView([p.lat, p.lng], 18);
        setAviso({ tipo: "ok", texto: `Marcado onde você está agora (precisão de ${Math.round(pos.coords.accuracy)} m).` });
      },
      () => setAviso({ tipo: "erro", texto: "Não consegui obter sua localização. Verifique a permissão do navegador." }),
      { enableHighAccuracy: true, timeout: 12000 }
    );
  }

  // Atalho para quem já tem a coordenada copiada do Google Maps. O mapa é
  // o caminho principal; isto evita obrigar quem já sabe o ponto a
  // procurá-lo de novo na tela.
  function aplicarColado(texto) {
    setColado(texto);
    const p = lerCoordenadas(texto);
    if (!p) {
      if (texto.trim()) setAviso({ tipo: "erro", texto: "Não reconheci essas coordenadas. Esperado algo como -3.743614, -38.536156." });
      return;
    }
    onChange(p);
    mapa.current?.setView([p.lat, p.lng], 17);
    setAviso({ tipo: "ok", texto: "Ponto posicionado pelas coordenadas coladas." });
  }

  const corAviso = aviso?.tipo === "erro" ? PERIGO : aviso?.tipo === "ok" ? "#059669" : CINZA_ESC;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Endereço com número e cidade é o que o OpenStreetMap acha com mais
          confiança; nome de instalação esportiva costuma faltar na base. Por
          isso o exemplo do campo é um endereço, e não o nome de um local. */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); buscarEndereco(); } }}
          placeholder="Endereço com número e cidade (ex: Av. Treze de Maio 2081, Fortaleza)"
          style={{
            flex: 1, minWidth: 240, border: "1px solid #D1D5DB", borderRadius: 10,
            padding: "9px 12px", fontSize: 14, fontFamily: "inherit",
            color: TEXTO, boxSizing: "border-box",
          }}
        />
        <button
          type="button"
          onClick={buscarEndereco}
          disabled={buscando}
          style={{
            border: "none", background: AZUL, color: "#fff", borderRadius: 10,
            padding: "9px 16px", fontSize: 13.5, fontWeight: 700,
            cursor: buscando ? "default" : "pointer",
          }}
        >
          {buscando ? "Buscando…" : "Buscar"}
        </button>
        <button
          type="button"
          onClick={usarMinhaLocalizacao}
          style={{
            border: `1.5px solid ${AZUL}`, background: "#fff", color: AZUL, borderRadius: 10,
            padding: "9px 14px", fontSize: 13.5, fontWeight: 700, cursor: "pointer",
          }}
        >
          📍 Estou aqui
        </button>
      </div>

      <div
        ref={divMapa}
        style={{
          height: alturaMapa, width: "100%", borderRadius: 8,
          border: "1px solid #D1D5DB", overflow: "hidden",
        }}
      />

      <div style={{ fontSize: 12, color: corAviso, lineHeight: 1.5 }}>
        {aviso
          ? aviso.texto
          : posicao
            ? `Ponto marcado. O círculo mostra os ${RAIO_FIXO} m em que a presença é aceita. Se a instalação for maior, cadastre outro ponto.`
            : "Busque pelo endereço, use “Estou aqui” se estiver no local, ou clique no mapa para marcar o ponto."}
      </div>

      <details>
        <summary style={{ fontSize: 12, color: CINZA_ESC, cursor: "pointer" }}>
          Já tenho as coordenadas
        </summary>
        <input
          value={colado}
          onChange={(e) => aplicarColado(e.target.value)}
          placeholder="-3.743614, -38.536156"
          style={{
            marginTop: 6, width: "100%", border: "1px solid #D1D5DB", borderRadius: 10,
            padding: "8px 11px", fontSize: 13, fontFamily: "inherit",
            color: TEXTO, boxSizing: "border-box",
          }}
        />
      </details>
    </div>
  );
}
